import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { NavigationActions } from '@next/core-lib/navigation';
import { SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WrapperSiteSidenavComponent } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import {
  BALANS_BEMESTING_CROP_FIELDS_ADMIN_APPLICATION_KEY,
  CROP_AID_ADMIN_APPLICATION_KEY,
  CROP_AID_ADMISSION_APPLICATION_KEY,
  BLIGHT_INFECTION_APPLICATION_KEY,
  FIELD_REPORT_APPLICATION_KEY,
  SPRAY_COCKTAILS_APPLICATION_KEY,
  CROP_AID_SYNERGIES_APPLICATION_KEY,
  TGA_VARIETIES_APPLICATION_KEY,
  USER_NOTIFICATIONS_APPLICATION_KEY,
  WATER_AUTHORITIES_APPLICATION_KEY,
  CROP_ADMIN_APPLICATION_KEY,
  ARABLE_PERIOD_APPLICATION_KEY,
  PLANET_PROOF_APPLICATION_KEY,
  FIELD_ADVICE_ADMIN_APPLICATION_KEY,
} from './shared/constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet, WrapperSiteSidenavComponent],
})
export class AppComponent implements OnInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly navigationItems = [
    {
      relativeUrl: '/field-advice/templates',
      label: 'templates',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-advice/automatic-advice',
      label: 'controlRoom.title',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-advice/nitrogen-model-properties',
      label: 'nitrogenModelProperties.title',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-advice/nutrient-properties',
      label: 'nutrientProperties.title',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-advice/production-purpose-category',
      label: 'productionPurposeCategory.title',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-advice/variety-properties',
      label: 'varietyProperties.title',
      applicationKey: FIELD_ADVICE_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/user-notifications',
      label: 'user-notifications.nav',
      applicationKey: USER_NOTIFICATIONS_APPLICATION_KEY,
    },
    {
      relativeUrl: '/tga-varieties',
      label: 'tgaVarieties.nav',
      applicationKey: TGA_VARIETIES_APPLICATION_KEY,
    },
    {
      relativeUrl: '/water-authorities',
      label: 'waterAuthorities.nav',
      applicationKey: WATER_AUTHORITIES_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-management/crops',
      label: 'cropAdmin.pages.crops.nav',
      applicationKey: CROP_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-management/cropvarieties',
      label: 'cropAdmin.pages.cropVarieties.nav',
      applicationKey: CROP_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-management/nutrients',
      label: 'cropAdmin.pages.nutrients.nav',
      applicationKey: CROP_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-management/legalrestrictions',
      label: 'cropAdmin.pages.legalRestrictions.nav',
      applicationKey: CROP_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-management/cropnutrientrequisites',
      label: 'cropAdmin.pages.cropNutrientRequisites.nav',
      applicationKey: CROP_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/crop-aid',
      label: 'cropAid.nav',
      applicationKey: CROP_AID_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/admission',
      label: 'cropAidAdmission.admissions.nav',
      applicationKey: CROP_AID_ADMISSION_APPLICATION_KEY,
    },
    {
      relativeUrl: '/ingredient',
      label: 'cropAidAdmission.ingredients.nav',
      applicationKey: CROP_AID_ADMISSION_APPLICATION_KEY,
    },
    {
      relativeUrl: '/cropaid-synergies',
      label: 'cropAidSynergies.nav',
      applicationKey: CROP_AID_SYNERGIES_APPLICATION_KEY,
    },
    {
      relativeUrl: '/bba-cancellation',
      label: 'bba-cancellation.nav',
      applicationKey: BALANS_BEMESTING_CROP_FIELDS_ADMIN_APPLICATION_KEY,
    },
    {
      relativeUrl: '/spray-cocktails',
      label: 'sprayCocktails.nav',
      applicationKey: SPRAY_COCKTAILS_APPLICATION_KEY,
    },
    {
      relativeUrl: '/blight-infection',
      label: 'blight-infection.title',
      applicationKey: BLIGHT_INFECTION_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/export',
      label: 'field-report.nav',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/total-nitrogen-legal-norm',
      label: 'field-report.navTotalNitrogenLegalNorm',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/legal-nitrogen-working-coefficient',
      label: 'field-report.navLegalNitrogenWorkingCoefficient',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/crop-variety-specific-norm',
      label: 'field-report.navCropVarietySpecificNorm',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/phosphate-legal-norm',
      label: 'field-report.navPhosphateLegalNorm',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/field-report/phosphate-legal-working-coefficient',
      label: 'field-report.navPhosphateLegalWorkingCoefficient',
      applicationKey: FIELD_REPORT_APPLICATION_KEY,
    },
    {
      relativeUrl: '/sales-order-schema',
      label: 'sales-order-periods.nav',
      applicationKey: ARABLE_PERIOD_APPLICATION_KEY,
    },
    {
      relativeUrl: '/planet-proof/cropsettings',
      label: 'planet-proof.cropSettings',
      applicationKey: PLANET_PROOF_APPLICATION_KEY,
    },
    {
      relativeUrl: '/planet-proof/activeingredientsettings',
      label: 'planet-proof.activeIngredientSettings',
      applicationKey: PLANET_PROOF_APPLICATION_KEY,
    },
  ];

  ngOnInit() {
    const userInitialized$ = this.store.select(UserSelectors.selectUserInitialized);

    const permissionStreams$ = this.navigationItems.map((item) =>
      this.store.select(AuthorizationSelectors.hasGlobalAdministratorRoleForApplicationKey(item.applicationKey)).pipe(filter((val) => val !== undefined)),
    );

    combineLatest([userInitialized$, ...permissionStreams$])
      .pipe(
        filter(([userInitialized]) => userInitialized),
        take(1),
      )
      .subscribe(([_, ...permissions]) => {
        const filteredNavigationItems = this.navigationItems.filter((_, index) => permissions[index]);

        this.store.dispatch(
          NavigationActions.setNavigationItems({
            navigationItems: filteredNavigationItems,
          }),
        );
      });
  }
}
